import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  TemplateRef,
  viewChild,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { WorkflowService } from '../../workflow.service';
import { WorkflowOwnersCategoryList } from '../../workflow.model';
import { BadgeComponent } from '../../../../../../ui-components/lib/badge/badge.component';
import { PaginatorComponent } from '../../../../../../ui-components/lib/paginator/paginator.component';
import { TableComponent } from '../../../../../../ui-components/lib/table/table.component';
import { UserDropdownViewComponent } from '../../../../../../ui-components/lib/user-dropdown-view/user-dropdown-view.component';
import { ModalServiceService } from '../../../../../../ui-components/lib/modal/modal-service.service';
import { ToasterService } from '../../../../../../ui-components/services/toast/toast.service';
import { TableColumns, TableFilterTab } from '../../../../../../ui-components/lib/table/table.model';
import { ActionListComponent } from '../../../../../common/components/action-list/action-list.component';
import { AssignGroupUsersComponent } from '../../../../../common/components/assign-group-users/assign-group-users.component';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import { ActionList } from '../../../../../common/models/action-list.model';
import { Option } from '../../../../../common/models/option.model';
import { DEFAULT_PAGE_SIZE_OPTIONS } from '../../../../../common/constants/pagination.constants';
import { LookupService } from '../../../../../common/services/lookup/lookup.service';

@Component({
  selector: 'lib-workflow-owners-category',
  imports: [
    BadgeComponent,
    TableComponent,
    TranslocoPipe,
    ActionListComponent,
    PaginatorComponent,
    AssignGroupUsersComponent,
    UserDropdownViewComponent,
  ],
  templateUrl: './workflow-owners-category-list.component.html',
  styleUrl: './workflow-owners-category-list.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class WorkflowOwnersCategoryListComponent implements OnInit {
  readonly pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS;

  router = inject(Router);
  transloco = inject(TranslocoService);
  userService = inject(LookupService);
  modalService = inject(ModalServiceService);
  workflowService = inject(WorkflowService);
  toastService = inject(ToasterService);
  tenantService = inject(TenantService);
  isLoading = signal<boolean>(false);
  isLoadingGroupUsers = signal<boolean>(false);
  currentPage = signal<number>(1);
  currentSize = signal<number>(25);
  totalPages = signal<number>(20);
  ownersCount = signal<string>('');
  groupUsersCards = signal<Option[]>([]);
  selectedUsers = signal<Option[]>([]);
  selectedUsersIds = signal<string[]>([]);
  selectedCategoryId = signal<string>('');

  modalCurrentPage = signal<number>(1);
  modalTotalPages = signal<number>(20);

  isOwnerLoading = signal<boolean>(false);
  isAssignOwnersLoading = signal<boolean>(false);
  ownerDropdownList = signal<any[]>([]);
  currentOwnerPage = signal<number>(1);
  totalOwnerPages = signal<number>(1);
  totalOwnerCount = signal<number>(0);

  assignOwnersDialog = viewChild<TemplateRef<unknown>>('assignOwnersDialog');
  permissionList = signal<WorkflowOwnersCategoryList[]>([]);
  viewDetailsAction: ActionList = {
    key: 'view_details',
    hasIcon: true,
    label: this.transloco.translate('view_details_action_item'),
    hasDivider: false,
    labelCustomStyle: '',
    hasToggle: false,
    toggleValue: false,
    isLink: true,
    routerLinks: [],
  } as ActionList;
  addOwnersAction: ActionList = {
    key: 'add',
    hasIcon: true,
    label: this.transloco.translate(
      'workflow_permissions_list_screen_add_owners_action_item'
    ),
    hasDivider: false,
    labelCustomStyle: 'text-primary-500 text-xs font-medium',
    hasToggle: false,
    toggleValue: false,
  } as ActionList;
  actionMenuItems: ActionList[] = [];
  columns: TableColumns[] = [
    { header: '', field: '' },
    {
      header: this.transloco.translate(
        'workflow_permissions_list_screen_table_header_category_name'
      ),
      field: '',
      showViewIcon: true,
    },
    {
      header: this.transloco.translate(
        'workflow_permissions_list_screen_table_header_category_owner'
      ),
      field: '',
    },

    { header: '', field: '' },
  ];
  tableFilterTabs: TableFilterTab[] = [
    {
      key: 'all',
      identifier: 'all',
      label: this.transloco.translate('table_filter_tab_all_label'),
      active: true,
      iconName: 'all',
    },
  ];

  ngOnInit() {
    this.getWorkflowCategoryPermissionList();
  }

  actionListItemClick(option: any, id: string) {
    if (option == 'add') {
      this.selectedCategoryId.set(id);
      this.openAssignOwnersDialog();
    }
  }

  openAssignOwnersDialog() {
    this.currentPage.set(1);
    this.getUsers();

    this.modalService.modalContent.set({
      isOpen: true,
      customizeContent: this.assignOwnersDialog(),
      canNotCloseFromOutside: true,
      width: '50.5',
    });
  }

  getUsers(name = '', page = 1) {
    this.modalCurrentPage.set(page);
    this.isLoadingGroupUsers.set(true);
    this.userService
      .userLookup({
        name,
        page: this.modalCurrentPage(),
        filtersRole: '',
        filtersNotPermissionForResource: 'own',
        filtersNotPermissionForResourceResourceType: 'flow_template_category',
        filtersNotPermissionForResourceResourceId: this.selectedCategoryId(),
        filtersNotState: 'inactive',
      })
      .subscribe(response => {
        const mappedData = response.data.map((user: any) => ({
          ...user,
          isSelected: this.selectedUsers().some(
            selectedUser => selectedUser.value === user.value
          ),
        }));

        if (this.modalCurrentPage() === 1) {
          this.groupUsersCards.set(mappedData);
        } else {
          this.groupUsersCards().push(...mappedData);
        }
        this.modalTotalPages.set(response.meta.last_page);
        this.isLoadingGroupUsers.set(false);
      });
  }

  handlePagination($event: number) {
    this.currentPage.set($event);
    this.getWorkflowCategoryPermissionList();
  }

  changeSize($event: number) {
    this.currentSize.set($event);
    this.currentPage.set(1);
    this.getWorkflowCategoryPermissionList();
  }

  clearSelectedUsers() {
    this.selectedUsers.set([]);
    this.selectedUsersIds.set([]);
  }

  onSelectGroupUser(user: Option) {
    user.isSelected = true;
    this.selectedUsers().unshift(user);

    this.groupUsersCards.set(
      this.groupUsersCards().filter(card => card.value !== user.value)
    );

    if (
      this.modalCurrentPage() < this.modalTotalPages() &&
      this.groupUsersCards().length < 7
    ) {
      this.getUsers('', this.modalCurrentPage() + 1);
    }
  }

  removeSelectedGroupUser(user: Option) {
    user.isSelected = false;
    this.selectedUsers.set(
      this.selectedUsers().filter(card => card.value !== user.value)
    );

    this.groupUsersCards().unshift(user);
  }

  closeModal() {
    this.modalService.modalContent.set({ isOpen: false });
  }

  updateSelectedUserIds() {
    this.selectedUsersIds().push(
      ...this.selectedUsers().map(user => user.value)
    );
  }

  onSubmitAssignedGroupUsers() {
    this.isAssignOwnersLoading.set(true);
    this.updateSelectedUserIds();
    this.workflowService
      .updateFlowTemplateCategory(this.selectedCategoryId(), {
        added_owners: this.selectedUsersIds(),
      })
      .subscribe({
        next: () => {
          this.clearSelectedUsers();
          this.closeModal();
          this.isAssignOwnersLoading.set(false);
          this.getWorkflowCategoryPermissionList();
        },
        error: (error: any) => {
          this.toastService.add(
            error.message,
            '',
            'assets/images/icons/error.svg',
            'error'
          );
          this.isAssignOwnersLoading.set(false);
          this.closeModal();
          this.clearSelectedUsers();
        },
        complete: () => {
          this.isAssignOwnersLoading.set(false);
          this.closeModal();
          this.clearSelectedUsers();
        },
      });
  }

  getWorkflowOwnerDropdown(event: any, id: string | number) {
    this.currentOwnerPage.set(event.currentPage);
    this.isOwnerLoading.set(true);
    if (this.currentOwnerPage() === 1) {
      this.ownerDropdownList.set([]);
      this.totalOwnerCount.set(0);
    }
    this.workflowService
      .getWorkflowOwnerDropdown(id, event.search, this.currentOwnerPage())
      .subscribe(response => this.handleOwnerDropdownSuccess(response));
  }

  handleOwnerDropdownSuccess(response: { data: any[]; meta: any }) {
    this.currentOwnerPage.set(response.meta.current_page);
    this.totalOwnerPages.set(response.meta.last_page);
    this.totalOwnerCount.set(response.meta.total);
    this.setOwnerDropdownList(response.data);
    this.isOwnerLoading.set(false);
  }

  setOwnerDropdownList(data: any[]) {
    if (this.currentOwnerPage() === 1) {
      this.ownerDropdownList.set(data);
    } else {
      this.ownerDropdownList().push(...data);
    }
  }

  getWorkflowCategoryPermissionList() {
    this.isLoading.set(true);
    this.workflowService
      .getWorkflowCategoryPermissionList(this.currentPage(), this.currentSize())
      .subscribe({
        next: (res: any) => {
          this.permissionList.set(res.data);
          this.ownersCount.set(res.meta.total);
          this.totalPages.set(res.meta.last_page);
        },
        complete: () => {
          this.isLoading.set(false);
        },
      });
  }

  viewCategoryDetails(category: any) {
    this.router.navigateByUrl(
      this.tenantService.adminPath(
        `workflow/owner-category/permission-details/${category.id}`
      )
    );
  }

  getActionMenuItems(id: number) {
    this.viewDetailsAction.routerLinks = [
      this.tenantService.adminPath(
        `workflow/owner-category/permission-details/${id}`
      ),
    ];
    this.actionMenuItems = [this.viewDetailsAction, this.addOwnersAction];
    return this.actionMenuItems;
  }
}
